import { InternalStore, StoreModel, StoresResponse } from './typing/store-locator.js'
import { STORE_LOCATOR_API, StoreLocatorApi, StoreLocatorGetRequest } from './apis/store-locator-api.js'
import { CSMapCoordinates, StoreLocatorMobileView } from './typing/internal.js'
import { GOOGLE_PLACES_API, GoogleAutocompletePlacesRequest, GooglePlacesApi } from './apis/google-places-api.js'
import { Autocomplete, AutocompleteModel } from './typing/google-autocomplete.js'
import { GOOGLE_GEOCODER_API, GoogleGeocodeApi, GoogleGeocodeRequest } from './apis/google-geocoder-api.js'
import { Geocodes } from './typing/google-geocoder.js'
import {
  STORE_MAP_CONFIGURATION_STORE,
  StoreMapConfigurationStore
} from 'components/discovery/store-locator/components/store-map/store-map-config-store.js'
import { FAKE, inject, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { GEOLOCATION } from '@cloudsuite/mosaic-shared/scripts/geolocation.js'
import { createSignal } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import typia from 'typia'
import { logValidationFailure } from 'components/shared/parsing/parsing.js'

export const STORES_PATH = 'api/v1/store/'
export const STORE_LOCATOR_PROXY_PATH = 'store-locator-proxy'

export type StoresList = {
  origin: 'preload' | 'headless',
  stores: InternalStore[],
}

export const defaultStoresList: StoresList = {
  origin: 'preload',
  stores: []
}

export const STORE_LOCATOR_STORE = Symbol('STORE_LOCATOR_STORE')

@registerDependency(REAL, STORE_LOCATOR_STORE)
@registerDependency(FAKE, STORE_LOCATOR_STORE)
export class StoreLocatorStore {

  @inject(STORE_MAP_CONFIGURATION_STORE)
  mapConfigStore!: StoreMapConfigurationStore

  @inject(STORE_LOCATOR_API)
  api!: StoreLocatorApi

  @inject(GOOGLE_PLACES_API)
  placesApi!: GooglePlacesApi

  @inject(GOOGLE_GEOCODER_API)
  geocodeApi!: GoogleGeocodeApi

  @inject(GEOLOCATION)
  geoLocation!: Geolocation

  stores: () => StoresList
  private setStores: (value: StoresList) => void

  selectedStore:() => InternalStore | null
  private setSelectedStore: (value: InternalStore | null) => void

  mapCoordinates:() => CSMapCoordinates | null
  private setMapCoordinates: (value: CSMapCoordinates | null) => void

  autocompleteResults: () => Autocomplete
  protected setAutocompleteResults: (value: Autocomplete) => void

  viewToShowOnMobile: () => StoreLocatorMobileView
  private setViewToShowOnMobile: (view: StoreLocatorMobileView) => void

  private storesPath: string = ''
  private proxy: string = ''
  private country: string = ''

  constructor() {
    [this.stores, this.setStores] = createSignal<StoresList>(defaultStoresList);
    [this.autocompleteResults, this.setAutocompleteResults] = createSignal<Autocomplete>(new AutocompleteModel());
    [this.selectedStore, this.setSelectedStore] = createSignal<InternalStore | null>(null);
    [this.mapCoordinates, this.setMapCoordinates] = createSignal<CSMapCoordinates | null>(null);
    [this.viewToShowOnMobile, this.setViewToShowOnMobile] = createSignal<StoreLocatorMobileView>('list')
  }

  autocompletePlaces = async (searchTerm: string) => {
    const request = new GoogleAutocompletePlacesRequest(
      searchTerm,
      this.mapConfigStore.mapsApiKey,
      this.proxy,
      this.country ? this.country : null
    )

    const predictions = await this.placesApi.searchPlaces(request)

    if(typia.is<Autocomplete>(predictions)) {
      this.setAutocompleteResults(predictions)
    } else {
      // eslint-disable-next-line no-console
      console.log('could not get autocomplete predictions')
    }
  }

  clearAutocompletion = () => {
    this.setAutocompleteResults(
      new AutocompleteModel()
    )
  }

  async geocodeLocate(searchTerm: string) {
    const geocodes = await this.geocodeApi.geocode(
      new GoogleGeocodeRequest(searchTerm, this.mapConfigStore.mapsApiKey, this.proxy)
    )

    if (typia.is<Geocodes>(geocodes) && geocodes.results.length > 0) {
      this.setMapCoordinates({
        type: 'autocomplete',
        location: {
          latitude: geocodes.results[0]?.geometry?.location?.lat || 0,
          longitude:  geocodes.results[0]?.geometry?.location?.lng || 0,
        }
      })
      this.setSelectedStore(null)
      this.getStores(this.mapCoordinates())
    } else {
      console.warn('Could not find any geocodes for this address')
    }
  }

  useCurrentLocation() {
    this.geoLocation.getCurrentPosition((position: GeolocationPosition) => {
      this.setMapCoordinates({
        type: 'current_location',
        location: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }
      })
      this.setSelectedStore(null)
      this.getStores(this.mapCoordinates())
    }, () => {
      console.warn('no permissions granted to use current location')
    })
  }

  selectStore(store: InternalStore) {
    this.setSelectedStore(store)
    if (
      store.address.geoLocation.latitude &&
      store.address.geoLocation.longitude
    ) {
      this.setMapCoordinates({
        type: this.mapCoordinates()?.type || '',
        location: {
          latitude: store.address.geoLocation.latitude,
          longitude: store.address.geoLocation.longitude,
        }
      })
      this.setViewToShowOnMobile('map')
    }
  }

  toggleListAndMapViews() {
    this.setViewToShowOnMobile(
      this.viewToShowOnMobile() === 'list' ? 'map' : 'list'
    )
  }

  preloadStores(stores: InternalStore[]) {
    this.setStores({
      origin: 'preload',
      stores: stores,
    })
  }

  preloadCoordinates(coords: CSMapCoordinates) {
    this.setMapCoordinates(coords)
  }

  setStoresPath = (path: string) => {
    this.storesPath = path
  }

  setProxy = (proxy: string) => { this.proxy = proxy }

  setCountry = (country: string) => { this.country = country }

  async getStores(coordinates: CSMapCoordinates | null = null) {
    this.api.getStores(
      new StoreLocatorGetRequest(this.storesPath, coordinates)
    ).then((response: unknown) => {
      const validated = typia.validate<StoresResponse>(response)
      if (validated.success) {
        const parsedStores = validated.data.stores.map(store => new StoreModel().with(store).parseToInternal())
        this.setStores({
          origin: 'headless',
          stores: parsedStores,
        })
      } else {
        logValidationFailure('<csc-store-locator> stores', validated.errors)
      }
    })
  }
}
